import { formatTZ } from "@repo/shared/utils";
import type { Agenda, AgendaItem, AgendaModule, Meeting } from "@repo/types";
import { multiline } from "@repo/ui/components/utilities/multiline";
import { MeetingIcon } from "./MeetingIcon";

export interface MeetingThumbnailProps {
  meeting: Meeting;
  onClick?: (id: string) => void;
  timeZone?: string;
}

export const MeetingThumbnail = ({
  meeting,
  onClick,
  timeZone,
}: MeetingThumbnailProps) => {
  return (
    <div
      className={`flex flex-col rounded-md w-52 h-52 bg-blue-50 pt-4 pb-2 pr-2 pl-2 ${
        onClick ? "cursor-pointer hover:bg-blue-100 transition-colors" : ""
      }`}
      onClick={onClick ? () => onClick(meeting.id) : undefined}
      tabIndex={onClick ? 0 : -1}
      role={onClick ? "button" : undefined}
    >
      <MeetingThumbnailHeader
        name={meeting.name}
        meetingType={meeting.meetingType}
      />
      <MeetingThumbnailBody meeting={meeting} timeZone={timeZone} />
      <MeetingThumbnailFooter date={meeting.startTime} timeZone={timeZone} />
    </div>
  );
};

export interface MeetingThumbnailHeaderProps {
  name: string;
  meetingType?: string;
}

export const MeetingThumbnailHeader = ({
  name,
  meetingType,
}: MeetingThumbnailHeaderProps) => {
  return (
    <div className="flex flex-row items-center justify-between pb-2">
      <h1 className="text-xs font-bold text-gray-800 truncate overflow-hidden text-ellipsis whitespace-nowrap">
        {name}
      </h1>
      <MeetingIcon
        type={meetingType ?? "other"}
        size={16}
        color="gray"
        className="flex-shrink-0 ml-2"
      />
    </div>
  );
};

export interface MeetingThumbnailHeaderFooterProps {
  date: Date;
  timeZone?: string;
}

export const MeetingThumbnailFooter = ({
  date,
  timeZone = "America/New_York",
}: MeetingThumbnailHeaderFooterProps) => {
  return (
    <div>
      <p className="text-[7pt] text-gray-600 text-center pt-2">
        {formatTZ(date, "MMM dd, yyyy", timeZone)} at{" "}
        {formatTZ(date, "h:mm a", "America/New_York")}
      </p>
    </div>
  );
};

export interface MeetingThumbnailBodyProps {
  meeting: Meeting;
  timeZone?: string;
}

export const MeetingThumbnailBody = ({
  meeting,
  timeZone = "America/New_York",
}: MeetingThumbnailBodyProps) => {
  return (
    <div className="rounded-md bg-white p-2 w-full h-full overflow-y-scroll">
      <h1 className="text-sm text-center">{meeting.name}</h1>
      <p className="text-[5px] text-center">
        {formatTZ(meeting.startTime, "MMMM dd, yyyy", timeZone)} at{" "}
        {formatTZ(meeting.startTime, "h:mm a", timeZone)}
      </p>
      <p className="text-[5px] text-center">{multiline(meeting.description)}</p>
      {meeting.agenda && (
        <div className="pl-1">
          <MeetingThumbnailAgenda agenda={meeting.agenda} />
        </div>
      )}
    </div>
  );
};

export interface MeetingThumbnailAgendaProps {
  agenda: Agenda;
}

export const MeetingThumbnailAgenda = ({
  agenda,
}: MeetingThumbnailAgendaProps) => {
  return (
    <div className="pt-1">
      {agenda.agendaModules &&
        agenda.agendaModules.map((module) => (
          <div className="pt-1" key={module.id}>
            <MeetingThumbnailAgendaModule
              key={module.id}
              agendaModule={module}
              index={agenda.agendaModules!.indexOf(module)}
            />
          </div>
        ))}
    </div>
  );
};

export interface MeetingThumbnailAgendaModuleProps {
  index: number;
  agendaModule: AgendaModule;
}

export const MeetingThumbnailAgendaModule = ({
  agendaModule,
  index,
}: MeetingThumbnailAgendaModuleProps) => {
  return (
    <div>
      <p className="text-[5px] font-bold">
        {index + 1}. {agendaModule.name}
      </p>
      <p className="pl-2 text-[4px] text-gray-400 pt-0">
        {multiline(agendaModule.description)}
      </p>
      <div className="pl-2">
        {agendaModule.agendaItems &&
          agendaModule.agendaItems.map((item) => (
            <div className="pt-1" key={item.id}>
              <MeetingThumbnailAgendaItem agendaItem={item} />
            </div>
          ))}
      </div>
    </div>
  );
};

export interface MeetingThumbnailAgendaItemProps {
  agendaItem: AgendaItem;
}

export const MeetingThumbnailAgendaItem = ({
  agendaItem,
}: MeetingThumbnailAgendaItemProps) => {
  return (
    <div>
      <p className="text-[5px]">{agendaItem.name}</p>
      <p className="pl-2 text-[4px] text-gray-400 pt-0">
        {multiline(agendaItem.description)}
      </p>
    </div>
  );
};
